/**
 * Consumer field declarations for the `/markets/[pubkey]` surface.
 *
 * `useGetMarket` now returns the complete fixed MarketDetailDto and callers do
 * not pass this declaration as `fields`. The arrays remain useful as local
 * auditable field/type declarations; projections for unrelated endpoints in
 * this module still go over the wire.
 *
 * Kept as a plain (non-`'use client'`) module so the route-level Server
 * Component can import these constants without pulling client code into the
 * server bundle.
 */

/**
 * Full superset of every fixed MarketDetailDto field consumed by the
 * detail-page orchestrator and per-section components.
 *
 * Trade activity uses its dedicated projected endpoint, so this market request
 * does not hydrate the unused embedded `recentTrades` subtree.
 */
export const MARKET_DETAIL_FIELDS = [
  'pubkey',
  'title',
  'ticker',
  'status',
  'coverImageUrl',
  'coverImageNsfw',
  'description',
  'category',
  'createdAt',
  'tradingStartedAt',
  'volumeTotal',
  'prizePool',
  'totalReserves',
  'outcomeCount',
  'uniqueTradersTotal',
  'volumeAtResolution',
  'uniqueTradersAtResolution',
  'marketConfig',
  'convictionUpdatedAt',
  'winnerOutcomeIndex',
  'resolvedAt',
  'merkleTreeCid',
  'claimTotalClaimable',
  'buyBurnPool',
  'boostTotal',
  'numBoosters',
  'outcomes',
  'creatorProfile',
] as const;

/**
 * `useGetTrades` projection for the Trades tab and thesis prompt. Every field
 * is consumed by those two surfaces; chart-only tape reads use the narrower
 * projection below.
 */
export const TRADES_PROJECTION = [
  'signature',
  'outcomeIndex',
  'type',
  'liquidityModel',
  'usdcAmount',
  'priceBefore',
  'priceAfter',
  'timestamp',
  'trader',
] as const;

/**
 * Viewer-scoped trade projection shared by the chart's marker annotations and
 * the Debate-tab thesis prompt. Keeping the exact fields identical makes both
 * consumers resolve to one SDK cache entry on the default detail view.
 *
 * `trader` is deliberately included even with the `user` filter: the realtime
 * subject is wildcard-scoped, so both consumers must reject another viewer's
 * prepended trade locally.
 */
export const VIEWER_TRADE_PROJECTION = [
  'eventId',
  'timestamp',
  'priceAfter',
  'priceBefore',
  'type',
  'side',
  'usdcAmount',
  'tokensAmount',
  'outcomeIndex',
  'totalCost',
  'signature',
  'thesis',
  'trader',
] as const;

/**
 * `useGetTrades` projection for the chart card's live trade-tape
 * (`allTradesQuery` in `connected-chart-container.tsx`) — the visible-fold
 * trade list on first paint. This is the SINGLE shape the market-detail SSR
 * prefetch hydrates: the live tape calls `useGetTrades({ market, fields })`
 * with NO `sortBy`/`sortOrder`, so the SSR prefetch must omit them too or the
 * cache key (`['trades','list',{market, …, sortBy, sortOrder}, fields]`)
 * misses and the tape refetches on mount.
 *
 * Kept distinct from `TRADES_PROJECTION` (the richer Trades-tab/feed set):
 * the tape only renders signature, outcome, type, USDC, price, and timestamp,
 * so a narrower wire payload is correct here.
 */
export const MARKET_TRADES_TAPE_FIELDS = [
  'signature',
  'outcomeIndex',
  'type',
  'side',
  'liquidityModel',
  'usdcAmount',
  'priceBefore',
  'priceAfter',
  'timestamp',
] as const;

/**
 * `useGetMarketTraders` projection for the Traders tab + the per-user "your
 * rank" card (collapsed from the deleted `/traders/rank/{userId}` route via
 * the `userId=` query filter — one entry returned).
 */
export const TRADERS_PROJECTION = [
  'participationId',
  'walletAddress',
  'profile',
  'realizedPnlD6',
  'realizedPnlGrossD6',
  'unrealizedPnlD6',
  'totalPnlD6',
  'volumeQuoteD6',
  'tradeCount',
  'lastTradeAt',
  'rank',
  'outcomes',
] as const;

/**
 * `useGetMarketBoosterLeaderboard` projection for the Boosters tab.
 */
export const BOOSTERS_PROJECTION = [
  'participationId',
  'rank',
  'booster',
  'totalAmount',
  'numBoosts',
  'lastBoostAt',
  'firstBoostAt',
] as const;

/**
 * `useGetMarketBackers` projection for the Backers tab leaderboard. Current
 * holders ranked largest→smallest by lifetime amount backed. Proposal consumers
 * also use this canonical participant source while backing is still open.
 */
export const BACKERS_PROJECTION = [
  'participationId',
  'rank',
  'backer',
  'totalBackedD6',
  'shareBalanceD6',
  'totalClaimedD6',
  'earningsD6',
  'lastBackedAt',
  'firstBackedAt',
] as const;

/**
 * Fields consumed by the Debate tab's per-market thesis feed.
 * `useGetTheses({ marketPubkey, sort: 'recent' })` returns the complete
 * fixed EnrichedThesis DTO; this array is not sent on the wire.
 */
export const THESES_PROJECTION = [
  'signature',
  'marketPubkey',
  'outcomeIndex',
  'outcomeLabel',
  'outcomeImageUrl',
  'outcomeImageNsfw',
  'content',
  'createdAt',
  'rating',
  'replyCount',
  'viewerReaction',
  'tradeDirection',
  'tradeAmount',
  'trader',
  'marketTitle',
  'marketCategory',
  'marketStatus',
] as const;

/**
 * Legacy SSR-only `useGetPositions` projection for the optional detail-page
 * holdings prefetch. Browser consumers use the canonical `POSITION_FIELDS`
 * scope from `useMarketPositions` instead.
 */
export const HOLDINGS_GATE_FIELDS = [
  'positionId',
  'outcomeIndex',
  'balance',
  'realizedPnlD6',
] as const;

/**
 * @deprecated v1 — kept for callsites still on the v1 SDK
 * `useGetMarketOutcomes` hook. These callsites are being migrated to read
 * outcomes off `useGetMarket.outcomes` in Phase 2E (SPEC 009 FILE 14). The
 * v1 hook's response shape includes `twarValue`/`twarLastUpdate` which the
 * v2 manifest renames to `conviction`/`convictionUpdatedAt` — keep this
 * constant until every v1 callsite is gone.
 */
export const MARKET_OUTCOMES_FIELDS = [
  'outcomeIndex',
  'label',
  'price',
  'twarValue',
  'twarLastUpdate',
  'reserves',
  'graduated',
  'mint',
  'totalSupply',
  'drained',
  'imageUrl',
  'imageNsfw',
] as const;

/**
 * Retained compatibility alias for local field/type declarations. Navigation
 * prefetches no longer pass it to the fixed DTO hook.
 */
export const MARKET_DETAIL_PREFETCH_FIELDS: typeof MARKET_DETAIL_FIELDS = MARKET_DETAIL_FIELDS;
